import IdsElement from '../../core/ids-element';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import styles from './ids-calendar-event-group.scss';
import { customElement, scss } from '../../core/ids-decorators';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import { attributes } from '../../core/ids-attributes';
import { IdsMonthViewToolTipSettings } from '../ids-month-view/ids-month-view-common';
import IdsGlobal from '../ids-global/ids-global';
import { CalendarEventData, CalendarEventTypeData } from './ids-calendar-event';

const Base = IdsLocaleMixin(
  IdsEventsMixin(
    IdsElement
  )
);

@customElement('ids-calendar-event-group')
@scss(styles)
export default class IdsCalendarEventGroup extends Base {
  // Property used to position overlapping events in month view
  #order = 0;

  cssClasses: string[] = [];

  #dateKey = '';

  cachedEvents: CalendarEventData[] | null = null;

  cachedEventType: CalendarEventTypeData | null = null;

  #tooltipSettings: IdsMonthViewToolTipSettings = {
    subject: true
  };

  #showTooltip = false;

  constructor(showTooltip?: boolean, tooltipSettings?: IdsMonthViewToolTipSettings) {
    super();

    this.#showTooltip = showTooltip ?? false;
    this.#tooltipSettings = tooltipSettings ?? this.#tooltipSettings;
  }

  /**
   * Returns array of observed attributes
   * @returns {Array<string>} attribute names
   */
  static get attributes(): string[] {
    return [
      ...super.attributes,
      attributes.HEIGHT,
      attributes.WIDTH,
      attributes.X_OFFSET,
      attributes.Y_OFFSET
    ];
  }

  /**
   * Invoked when ids-calendar-event is added to the DOM
   */
  connectedCallback(): void {
    super.connectedCallback();
    this.#attachEventHandlers();
    this.refreshContent();
    this.recalc();
  }

  /**
   * Creates template for ids calendar event
   * @returns {string} html
   */
  template(): string {
    return `
      <a class="ids-calendar-event-group" href="#" color="${this.color}">
        ${this.contentTemplate()}
      </a>
    `;
  }

  /**
   * Creates template for calendar event content
   * @returns {string} content html
   */
  contentTemplate(): string {
    if (!this.eventTypeData) return ``;

    const text = this.eventTypeData.label;
    const numberOfEvents = this.eventData?.length;
    const overflow = this.overflow;
    const icon = this.eventTypeData?.icon
      ? `<ids-icon class="calendar-event-icon" icon="${this.eventTypeData.icon}" height="14" width="14"></ids-icon>` : '';

    return `<div class="calendar-event-group-content">
      <div class="header">
        ${icon}
        <ids-text class="calendar-event-title" inline font-size="12" ${!this.#showTooltip ? `overflow="${overflow}"` : ''} tooltip="${text}">
        ${text}
        </ids-text>
        <ids-tag width="14px" height="14px" class="calendar-counter">${numberOfEvents}</ids-tag>
      </div>
    </div>`;
  }

  /**
   * Attach calendar-event event handlers
   */
  #attachEventHandlers(): void {
  }

  /** Respond to language changes */
  onLanguageChange = () => {
    this.refreshContent();
  };

  /**
   * Refreshses calendar event content with current settings
   */
  refreshContent(): void {
    if (this.container) {
      this.container.innerHTML = this.contentTemplate();
    }
  }

  /**
   * Updates calendar event dimensions and position
   */
  recalc(): void {
    if (!this.container) return;

    // resize
    if (this.height) {
      this.container.style.height = this.height;
    }

    if (this.width) {
      this.container.style.width = this.width;
    }

    // reposition
    if (this.yOffset) {
      this.container.style.top = this.yOffset;
    }

    if (this.localeAPI?.isRTL()) {
      this.container.style.right = this.xOffset || '';
      this.container.style.removeProperty('left');
    } else {
      this.container.style.removeProperty('right');
      this.container.style.left = this.xOffset || '';
    }
  }

  get localeAPI() {
    return IdsGlobal.getLocale();
  }

  /**
   * Sets calendar event data
   * @param {CalendarEventData} data Event data
   */
  set eventData(data: CalendarEventData[] | undefined | null) {
    this.cachedEvents = data ?? null;
    if (data && data.length > 0) this.setAttribute(attributes.EVENT_TYPE_ID, data[0].type);
    this.refreshContent();
  }

  /**
   * Gets calendar event data
   * @returns {CalendarEventData} Event data
   */
  get eventData(): CalendarEventData[] | null {
    return this.cachedEvents;
  }

  /**
   * Sets calendar event type
   * @param {CalendarEventTypeData} data Event type
   */
  set eventTypeData(data: CalendarEventTypeData | undefined | null) {
    this.cachedEventType = data ?? null;

    // update cached event data
    if (this.cachedEvents && this.cachedEvents.length > 0 && data?.id) {
      this.cachedEvents[0].type = data?.id;
    }

    if (data?.color) {
      this.container?.setAttribute('color', data?.color);
    } else {
      this.container?.removeAttribute('color');
    }
  }

  /**
   * Gets calendar event type
   * @returns {CalendarEventTypeData} Event type
   */
  get eventTypeData(): CalendarEventTypeData | null {
    return this.cachedEventType;
  }

  /**
   * Sets top style of calendar event
   * @param {string | null} value css top value
   */
  set yOffset(value: string | null) {
    if (value) {
      this.setAttribute(attributes.Y_OFFSET, value);
    } else {
      this.removeAttribute(attributes.Y_OFFSET);
    }

    this.recalc();
  }

  /**
   * Gets y offset
   * @returns {string | null} yOffset value
   */
  get yOffset(): string | null {
    return this.getAttribute(attributes.Y_OFFSET);
  }

  /**
   * Sets horizontal position of calendar event
   * Styles left or right depending on rtl flag
   * @param {string | null} value css left/right value
   */
  set xOffset(value: string | null) {
    if (value) {
      this.setAttribute(attributes.X_OFFSET, value);
    } else {
      this.removeAttribute(attributes.X_OFFSET);
    }

    this.recalc();
  }

  /**
   * @returns {string | null} xOffset value
   */
  get xOffset(): string | null {
    return this.getAttribute(attributes.X_OFFSET);
  }

  /**
   * Sets height of calendar event (Defaults to 20px)
   * @param {string | null} value css height value
   */
  set height(value: string | null) {
    if (value) {
      this.setAttribute(attributes.HEIGHT, value);
    } else {
      this.removeAttribute(attributes.HEIGHT);
    }

    this.recalc();
  }

  /**
   * Gets height
   * @returns {string | null} height value
   */
  get height(): string | null {
    return this.getAttribute(attributes.HEIGHT);
  }

  /**
   * Sets width of calendar event (Defaults to width of container)
   * @param {string | null} value css width value
   */
  set width(value: string | null) {
    if (value) {
      this.setAttribute(attributes.WIDTH, value);
    } else {
      this.removeAttribute(attributes.WIDTH);
    }

    this.recalc();
  }

  /**
   * Gets width
   * @returns {string | null} width value
   */
  get width(): string | null {
    return this.getAttribute(attributes.WIDTH);
  }

  /**
   * Sets extra css classes to calendar event
   * @param {Array<string>} value array of css classes
   */
  set cssClass(value: string[]) {
    this.cssClasses = this.cssClasses.concat(value);
    this.container?.classList.add(...value);
  }

  /**
   * Sets overflow type for IdsText
   * @param {string} value Overflow values for IdsText
   */
  set overflow(value: string | null) {
    this.setAttribute(attributes.OVERFLOW, value ?? 'ellipsis');
    this.refreshContent();
  }

  /**
   * Gets overflow value of IdsText
   * @returns {string} IdsText overflow setting value
   */
  get overflow(): string {
    return this.getAttribute(attributes.OVERFLOW) || 'ellipsis';
  }

  /**
   * Sets order property
   * @param {number} val order number
   */
  set order(val: number) {
    this.#order = val;
  }

  /**
   * Gets order property
   * @returns {number} order number
   */
  get order(): number {
    return this.#order;
  }

  /**
   * Gets color property from event type data
   * @returns {string} color
   */
  get color(): string {
    return this.eventTypeData?.color || 'blue';
  }

  /**
   * Sets dateKey property
   * @param {string} val dateKey string
   */
  set dateKey(val: string) {
    this.#dateKey = val;
  }

  /**
   * Gets dateKey property
   * @returns {string} dateKey string
   */
  get dateKey(): string {
    return this.#dateKey;
  }

  /**
   * Set disabled property
   * @param {boolean} val disabled value
   */
  set disabled(val: boolean) {
    if (stringToBool(val)) {
      this.setAttribute(attributes.DISABLED, '');
    } else {
      this.removeAttribute(attributes.DISABLED);
    }
  }

  /**
   * Get disabled property
   * @returns {boolean} disabled value
   */
  get disabled(): boolean {
    return stringToBool(this.getAttribute(attributes.DISABLED));
  }

  /**
   * Gets the tooltip settings
   * @returns {IdsMonthViewToolTipSettings} the tooltip settings
   */
  get tooltipSettings(): IdsMonthViewToolTipSettings {
    return this.#tooltipSettings;
  }

  /**
   * sets the tooltip settings
   * @param {IdsMonthViewToolTipSettings} settings the tooltip settings
   */
  set tooltipSettings(settings: IdsMonthViewToolTipSettings) {
    this.#tooltipSettings = settings;
  }
}
